import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import AssistantCitationSchema from "ai-assistant/citations/assistant-citation-schema";
import AssistantGroundingSchema from "ai-assistant/grounding/assistant-grounding-schema";
import StagedActionSchema from "ai-assistant/staged-actions/staged-action-schema";
import { Role } from "ai-assistant/roles/assistant-role";
import { AssistantResponseStatus } from "ai-assistant/statuses/assistant-response-status";
import { z } from "zod";

export type AssistantResponseJSON = z.infer<typeof AssistantResponseSchema.AssistantResponseJSONZ>;
export type AssistantResponseParams = z.infer<typeof AssistantResponseSchema.AssistantResponseZ>;

export default class AssistantResponseSchema {
  public static readonly AssistantResponseJSONZ = z.object({
    conversationId: z.uuid().nullish(),
    role: z.enum(Role),
    text: z.string(),
    citations: z.array(AssistantCitationSchema.AssistantCitationJSONZ),
    timestamp: z.iso.datetime({ offset: true }),
    // Absent on a reply from an API that predates the field; absent, not a failure.
    status: z.enum(AssistantResponseStatus).nullish(),
    errorCode: z.string().nullish(),
    // Omitted entirely when the turn prepared nothing, which is every turn until a write
    // capability is switched on. A prepared action is still an Answered reply - its
    // presence here is the signal, not the status.
    stagedActions: z.array(StagedActionSchema.StagedActionJSONZ).nullish(),
    // Absent on a failed or conflicted turn, where nothing was verified, and on a reply from
    // an API that predates the field. Absent is read as NotApplicable rather than as
    // Grounded: an unchecked answer must not arrive looking like a checked one.
    grounding: AssistantGroundingSchema.AssistantGroundingJSONZ.nullish(),
  });

  public static readonly AssistantResponseZ = z.object({
    conversationId: z.string().optional(),
    role: z.enum(Role),
    text: z.string(),
    citations: z.array(AssistantCitationSchema.AssistantCitationZ),
    timestamp: z.custom<AHDate>(),
    // Optional so a locally-built message - the user's own turn, an optimistic echo -
    // does not have to state a status it has no opinion about. It defaults to Answered.
    status: z.enum(AssistantResponseStatus).optional(),
    errorCode: z.string().optional(),
    stagedActions: z.array(StagedActionSchema.StagedActionZ).optional(),
    grounding: AssistantGroundingSchema.AssistantGroundingZ.optional(),
    // Client-side only, and deliberately absent from the JSON shape above: the server sends
    // prepared actions or it does not, and has no way to say "there were some and you are not
    // getting them". That sentence belongs to a transcript being replayed, so it is set when
    // one is built rather than received. See `AssistantConversation.toTranscript`.
    preparedActionsNotReplayed: z.boolean().optional(),
  });

  public static validate(values: unknown): AssistantResponseParams {
    return parseOrThrow(
      AssistantResponseSchema.AssistantResponseZ, 
      values, 
      { 
        logError: true,
        schemaName: "AssistantResponseSchema",
        operation: "validate",
        payloadSample: values 
      }
    );
  }

  public static validateJSON(json: unknown): AssistantResponseJSON {
    return parseOrThrow(
      AssistantResponseSchema.AssistantResponseJSONZ, 
      json, 
      { 
        logError: true,
        schemaName: "AssistantResponseSchema",
        operation: "validateJSON",
        payloadSample: json 
      }
    );
  }

  public static isValid(values: unknown): values is AssistantResponseParams {
    try {
      AssistantResponseSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is AssistantResponseJSON {
    try {
      AssistantResponseSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: AssistantResponseJSON): AssistantResponseParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(
      AssistantResponseSchema.AssistantResponseJSONZ, 
      json, 
      { 
        logError: true,
        schemaName: "AssistantResponseSchema",
        operation: "deserialize",
        payloadSample: json 
      }
    );
    return AssistantResponseSchema.codec.decode(parsed);
  }

  public static serialize(legalEntity: AssistantResponseParams): AssistantResponseJSON {
    if (isNullOrUndefined(legalEntity)) return legalEntity;
    const parsed = AssistantResponseSchema.validate(legalEntity);
    if (isNullOrUndefined(parsed)) return parsed;
    return AssistantResponseSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    AssistantResponseSchema.AssistantResponseJSONZ,
    AssistantResponseSchema.AssistantResponseZ,
    {
      decode: (data) => ({
        conversationId: data.conversationId ?? undefined,
        role: data.role as Role,
        text: data.text,
        citations: data.citations.map((citation) => AssistantCitationSchema.deserialize(citation)),
        timestamp: new AHDate(data.timestamp),
        // Answered is 0, so `?? Answered` rather than `|| Answered`: the nullish
        // coalescing is what keeps a real, answered reply from being re-defaulted.
        status: data.status ?? AssistantResponseStatus.Answered,
        errorCode: data.errorCode ?? undefined,
        stagedActions: (data.stagedActions ?? []).map(
          (action) => StagedActionSchema.deserialize(action)
        ),
        grounding: isNullOrUndefined(data.grounding)
          ? undefined
          : AssistantGroundingSchema.deserialize(data.grounding),
        preparedActionsNotReplayed: undefined,
      }),
      encode: (response) => ({
        conversationId: response.conversationId,
        role: response.role,
        text: response.text,
        citations: response.citations.map((citation) => AssistantCitationSchema.serialize(citation)),
        timestamp: response.timestamp.toISOString(),
        status: response.status,
        errorCode: response.errorCode,
        stagedActions: (response.stagedActions ?? []).map(
          (action) => StagedActionSchema.serialize(action)
        ),
        grounding: isNullOrUndefined(response.grounding)
          ? undefined
          : AssistantGroundingSchema.serialize(response.grounding),
      }),
    }
  );
}
